/* global URLSearchParams, fetch */

export class ConfigLoadError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "ConfigLoadError";
    this.code = code;
  }
}

export function parseTenantToken(search) {
  const params = new URLSearchParams(search);
  const token = params.get("t");
  if (!token) {
    throw new ConfigLoadError("MISSING_TOKEN", "No tenant token (?t=) in the task pane URL.");
  }
  if (!/^[A-Za-z0-9_-]+$/.test(token)) {
    throw new ConfigLoadError("INVALID_TOKEN", "The tenant token has an invalid format.");
  }
  return token;
}

// Defense in depth only — the real boundary is the CSP connect-src in
// taskpane.html and webpack.config.js's devServer.headers, enforced by the
// browser regardless of what this file does. Keep these three lists in sync
// when a new gateway origin is added.
export const ALLOWED_API_ORIGINS = ["https://dev.flowsgw.reshapex.ai"];

export function isAllowedApiOrigin(url, allowlist = ALLOWED_API_ORIGINS) {
  return allowlist.includes(url);
}

const GUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// Azure AD app (client) IDs are always GUIDs — validating the shape here
// catches a copy-paste mistake (e.g. pasting the Application ID URI instead
// of the bare client ID) at config-load time instead of failing opaquely
// inside MSAL later.
export function isValidClientId(clientId) {
  return typeof clientId === "string" && GUID_PATTERN.test(clientId);
}

export async function loadTenantConfig(search, fetchImpl = fetch) {
  const token = parseTenantToken(search);

  const response = await fetchImpl(`configs/${token}.json`);
  if (!response.ok) {
    throw new ConfigLoadError("CONFIG_NOT_FOUND", `No config found for token "${token}".`);
  }

  const config = await response.json();
  const { apiUrl, deploymentId, identity } = config;
  if (!apiUrl || !deploymentId) {
    throw new ConfigLoadError(
      "INCOMPLETE_CONFIG",
      "Tenant config is missing apiUrl or deploymentId."
    );
  }
  if (!isAllowedApiOrigin(apiUrl)) {
    throw new ConfigLoadError(
      "DISALLOWED_ORIGIN",
      `apiUrl "${apiUrl}" is not in the allowed origins list.`
    );
  }
  if (identity !== undefined) {
    if (typeof identity !== "object" || identity === null || !isValidClientId(identity.clientId)) {
      throw new ConfigLoadError(
        "INVALID_IDENTITY_CONFIG",
        "Tenant config's identity.clientId must be a GUID (the Azure AD app registration's Application (client) ID)."
      );
    }
  }

  return { apiUrl, deploymentId, identity };
}
